import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { TrendingUp, Award, Target, BookOpen, ChevronRight, Star, Zap, Crown, ArrowRight, Lock } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const tierConfig = {
  starter: { 
    label: "Starter", 
    color: "#94A3B8",
    icon: Star,
    benefits: ["Basic session rates", "Standard support", "Core training access"],
    requirements: [],
    incomeRange: "RM 2,000 - 3,500"
  },
  pro: { 
    label: "Pro", 
    color: "#60A5FA",
    icon: Zap,
    benefits: ["+10% rate bonus", "Priority booking", "Phone support", "Advanced training"],
    requirements: ["80% performance score", "50+ completed sessions", "4.0+ rating"],
    incomeRange: "RM 3,500 - 5,500"
  },
  elite: { 
    label: "Elite", 
    color: "#F59E0B",
    icon: Award,
    benefits: ["+20% rate bonus", "Premium booking", "Featured profile", "Specialist training"],
    requirements: ["88% performance score", "100+ completed sessions", "4.5+ rating"],
    incomeRange: "RM 5,500 - 8,000"
  },
  master: { 
    label: "Master", 
    color: "#1E293B",
    icon: Crown,
    benefits: ["+30% rate bonus", "VIP access", "Personal manager", "Training opportunities"],
    requirements: ["95% performance score", "200+ completed sessions", "4.8+ rating"],
    incomeRange: "RM 8,000+"
  },
};

const tierOrder = ["starter", "pro", "elite", "master"];

// Mock recommended training
const recommendedTraining = [
  { id: 1, title: "Advanced Manual Therapy", duration: "4 hours", impact: "+5% performance" },
  { id: 2, title: "Pediatric Assessment", duration: "3 hours", impact: "Unlock pediatric cases" },
  { id: 3, title: "Geriatric Care Specialist", duration: "5 hours", impact: "+10% senior cases" },
];

// Mock skill gaps
const skillGaps = [
  { skill: "Manual Therapy", current: 70, target: 85 },
  { skill: "Neurological Rehab", current: 55, target: 80 },
  { skill: "Pediatric Care", current: 30, target: 70 },
];

export default function TherapistCareer() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/therapist/stats`, { withCredentials: true });
        setStats(response.data);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const currentTierIndex = tierOrder.indexOf(stats?.tier || "starter");
  const currentTier = tierConfig[stats?.tier || "starter"];

  return (
    <div className="space-y-6" data-testid="therapist-career">
      {/* Current Status */}
      <Card className="bg-gradient-to-br from-slate-50 to-white">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div 
                className="w-20 h-20 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: `${currentTier.color}20` }}
              >
                <currentTier.icon className="w-10 h-10" style={{ color: currentTier.color }} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Current Tier</p>
                <h2 className="font-heading font-bold text-3xl" style={{ color: currentTier.color }}>
                  {currentTier.label}
                </h2>
                <p className="text-slate-600">Expected income: {currentTier.incomeRange}/month</p>
              </div>
            </div>
            <div className="text-center md:text-right">
              <p className="text-sm text-slate-500">Performance Score</p>
              <p className="font-heading font-bold text-4xl text-slate-900">{stats?.performance_score}%</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tier Roadmap */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            Career Roadmap
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            {/* Progress Line */}
            <div className="absolute top-8 left-0 right-0 h-1 bg-slate-200 z-0">
              <div 
                className="h-full bg-primary transition-all duration-500"
                style={{ width: `${((currentTierIndex + 1) / 4) * 100}%` }}
              />
            </div>
            
            {/* Tier Steps */}
            <div className="relative z-10 flex justify-between">
              {tierOrder.map((tierKey, index) => {
                const tier = tierConfig[tierKey];
                const Icon = tier.icon;
                const isActive = index === currentTierIndex;
                const isCompleted = index < currentTierIndex;
                const isLocked = index > currentTierIndex;
                
                return (
                  <div key={tierKey} className="flex flex-col items-center">
                    <div 
                      className={`w-16 h-16 rounded-full flex items-center justify-center border-4 ${
                        isActive ? 'border-primary bg-white' :
                        isCompleted ? 'bg-white' : 'bg-slate-100'
                      }`}
                      style={{ borderColor: isActive || isCompleted ? tier.color : '#e2e8f0' }}
                    >
                      {isLocked ? (
                        <Lock className="w-6 h-6 text-slate-400" />
                      ) : (
                        <Icon className="w-6 h-6" style={{ color: tier.color }} />
                      )}
                    </div>
                    <p className={`mt-2 font-medium text-sm ${isActive ? 'text-slate-900' : 'text-slate-500'}`}>
                      {tier.label}
                    </p>
                    <p className="text-xs text-slate-400">{tier.incomeRange}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Next Tier Requirements */}
          {currentTierIndex < 3 && (
            <div className="mt-8 p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <ArrowRight className="w-5 h-5 text-primary" />
                <p className="font-medium text-slate-900">
                  To reach {tierConfig[tierOrder[currentTierIndex + 1]].label}:
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {tierConfig[tierOrder[currentTierIndex + 1]].requirements.map((req, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                    <div className="w-2 h-2 rounded-full bg-primary"></div>
                    {req}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Skill Gaps */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Skill Development</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {skillGaps.map((skill) => (
                <div key={skill.skill} className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium text-slate-900">{skill.skill}</p>
                    <Badge variant="secondary">
                      {skill.current}% / {skill.target}%
                    </Badge>
                  </div>
                  <div className="relative">
                    <Progress value={skill.current} className="h-3" />
                    <div 
                      className="absolute top-0 h-3 w-0.5 bg-slate-400"
                      style={{ left: `${skill.target}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    {skill.target - skill.current}% more to reach target
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recommended Training */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              Recommended Training
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recommendedTraining.map((course) => (
                <div 
                  key={course.id} 
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                >
                  <div>
                    <p className="font-medium text-slate-900">{course.title}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-slate-500">{course.duration}</span>
                      <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                        {course.impact}
                      </Badge>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              ))}
            </div>
            <Button className="w-full mt-4" variant="outline" data-testid="view-all-courses-btn">
              View All Courses
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Income Projection */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-500" />
            Income Projection
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {tierOrder.map((tierKey) => {
              const tier = tierConfig[tierKey];
              const isCurrentOrHigher = tierOrder.indexOf(tierKey) >= currentTierIndex;
              
              return (
                <div 
                  key={tierKey}
                  className={`p-4 rounded-lg text-center ${
                    tierKey === stats?.tier 
                      ? 'bg-primary text-white' 
                      : isCurrentOrHigher 
                        ? 'bg-slate-50' 
                        : 'bg-slate-100 opacity-50'
                  }`}
                >
                  <tier.icon className={`w-8 h-8 mx-auto mb-2 ${tierKey === stats?.tier ? 'text-white' : ''}`} style={{ color: tierKey === stats?.tier ? 'white' : tier.color }} />
                  <p className={`font-heading font-bold ${tierKey === stats?.tier ? 'text-white' : 'text-slate-900'}`}>
                    {tier.label}
                  </p>
                  <p className={`text-sm ${tierKey === stats?.tier ? 'text-white/80' : 'text-slate-500'}`}>
                    {tier.incomeRange}
                  </p>
                </div>
              );
            })}
          </div>
          <div className="mt-6 p-4 bg-emerald-50 rounded-lg">
            <p className="text-emerald-800">
              <strong>Projection:</strong> By completing recommended training and maintaining performance, 
              you could reach <strong>{tierConfig[tierOrder[Math.min(currentTierIndex + 1, 3)]].label}</strong> tier 
              within <strong>3-4 months</strong>, increasing your potential income to <strong>
              {tierConfig[tierOrder[Math.min(currentTierIndex + 1, 3)]].incomeRange}</strong>/month.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
